import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Phone, Printer, Mail, Globe, ShieldCheck, MessageSquare, Sprout, Instagram, Facebook, Music2, Youtube, Twitter } from "lucide-react";
import Seo from "@/components/Seo";
import ShareButtons from "@/components/ShareButtons";
import Testimonials from "@/components/Testimonials";
import TestimonialForm from "@/components/TestimonialForm";
import OnlineBadge from "@/components/OnlineBadge";
import { resolveImageUrl } from "@/components/ImageUploader";

const Row = ({ icon: Icon, label, value, testid, href }) => {
  if (!value) return null;
  const inner = (
    <div data-testid={testid} className="flex items-start gap-3 text-sm text-stone-700 font-sans">
      <Icon size={14} className="text-emerald-800 mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</div>
        <div className="mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="hover:text-emerald-800">{inner}</a> : inner;
};

export default function HobbyFarmerProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/hobby-farmers/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const messageFarmer = async () => {
    if (!user) {
      toast.info("Sign in to message this hobby farmer.");
      navigate("/login");
      return;
    }
    try {
      await api.post("/messages", { to_user_id: userId, text: `Hi ${data.farmer.farm_name} — I'm interested in your listings.` });
      toast.success("Message sent. Check your dashboard.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-stone-500 font-sans">Loading hobby farmer profile...</div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <Sprout className="mx-auto text-stone-400" size={32} />
          <div className="mt-4 font-serif text-2xl text-stone-700">Profile not found.</div>
          <p className="mt-2 text-stone-500 font-sans text-sm">This hobby farmer hasn't completed their public profile yet.</p>
          <Link to="/hobby-farmers"><Button className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-stone-50">Back to Directory</Button></Link>
        </div>
      </div>
    );
  }

  const { farmer, products, newsletters } = data;
  const fullAddress = [farmer.street_address, farmer.city, farmer.state, farmer.country].filter(Boolean).join(", ");

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-12 py-12">
      <Seo
        url={`/hobby-farmers/${farmer.user_id}`}
        title={farmer.farm_name}
        description={`${farmer.farm_name} in ${[farmer.city, farmer.state].filter(Boolean).join(", ")}. ${(farmer.services || "").slice(0, 140)}`}
        type="profile"
      />
      <div className="bg-white border border-stone-200 rounded-md p-8 sm:p-12">
        {farmer.profile_image_url && (
          <div className="-mx-8 -mt-8 sm:-mx-12 sm:-mt-12 mb-8 aspect-[3/1] overflow-hidden rounded-t-md bg-stone-100">
            <img src={resolveImageUrl(farmer.profile_image_url)} alt={farmer.farm_name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-start gap-8">
          <div className="flex-1">
            <div className="flex items-start gap-3">
              <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 leading-tight">{farmer.farm_name}</h1>
              {farmer.verified && (
                <Badge className="bg-emerald-800 text-stone-50 mt-2"><ShieldCheck size={12} className="mr-1" />Verified</Badge>
              )}
            </div>
            <div className="mt-2 font-sans text-stone-500 text-sm flex items-center gap-3 flex-wrap">
              <span>Owned by {farmer.name}</span>
              <OnlineBadge isOnline={farmer.is_online} />
            </div>
            {farmer.services && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Products & Services</div>
                <p className="mt-2 font-sans text-stone-700 leading-relaxed whitespace-pre-line">{farmer.services}</p>
              </div>
            )}
            {farmer.bio && (
              <p className="mt-6 font-sans text-stone-600 leading-relaxed whitespace-pre-line">{farmer.bio}</p>
            )}
          </div>

          <div className="md:w-80 shrink-0 bg-stone-50 border border-stone-200 rounded-md p-6 flex flex-col gap-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Contact</div>
            <Row testid="profile-address" icon={MapPin} label="Address" value={fullAddress || null} />
            <Row testid="profile-phone" icon={Phone} label="Phone" value={farmer.phone} href={farmer.phone ? `tel:${farmer.phone.replace(/\s/g, "")}` : null} />
            <Row testid="profile-fax" icon={Printer} label="Fax" value={farmer.fax} />
            <Row testid="profile-website" icon={Globe} label="Website" value={farmer.website} href={farmer.website && (farmer.website.startsWith("http") ? farmer.website : `https://${farmer.website}`)} />

            {(farmer.instagram || farmer.facebook || farmer.tiktok || farmer.youtube || farmer.twitter) && (
              <div className="pt-4 border-t border-stone-200" data-testid="profile-socials">
                <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold mb-3">Follow this farm</div>
                <div className="flex gap-2 flex-wrap">
                  {farmer.instagram && (
                    <a href={`https://instagram.com/${farmer.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center hover:bg-emerald-800 transition-colors" title="Instagram" data-testid="profile-instagram">
                      <Instagram size={16} />
                    </a>
                  )}
                  {farmer.facebook && (
                    <a href={`https://facebook.com/${farmer.facebook}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center hover:bg-emerald-800 transition-colors" title="Facebook" data-testid="profile-facebook">
                      <Facebook size={16} />
                    </a>
                  )}
                  {farmer.tiktok && (
                    <a href={`https://tiktok.com/@${farmer.tiktok.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center hover:bg-emerald-800 transition-colors" title="TikTok" data-testid="profile-tiktok">
                      <Music2 size={16} />
                    </a>
                  )}
                  {farmer.youtube && (
                    <a href={farmer.youtube.startsWith("http") ? farmer.youtube : `https://youtube.com/${farmer.youtube.startsWith("@") ? farmer.youtube : "@" + farmer.youtube}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center hover:bg-emerald-800 transition-colors" title="YouTube" data-testid="profile-youtube">
                      <Youtube size={16} />
                    </a>
                  )}
                  {farmer.twitter && (
                    <a href={`https://x.com/${farmer.twitter.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-900 text-stone-50 flex items-center justify-center hover:bg-emerald-800 transition-colors" title="X" data-testid="profile-twitter">
                      <Twitter size={16} />
                    </a>
                  )}
                </div>
              </div>
            )}

            <Button
              data-testid="message-farmer-btn"
              onClick={messageFarmer}
              className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-stone-50 rounded-md"
            >
              <MessageSquare size={14} className="mr-2" />Message this hobby farmer
            </Button>
            <div className="pt-4 border-t border-stone-200">
              <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold mb-3">Share this farm</div>
              <ShareButtons
                url={`/hobby-farmers/${farmer.user_id}`}
                title={`${farmer.farm_name} on Azure Gypsy Farms`}
                summary={[farmer.city, farmer.state, farmer.country].filter(Boolean).join(", ")}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Listings ({products.length})</div>
        <h2 className="mt-3 font-serif text-3xl text-stone-900">What's growing right now</h2>
        {products.length === 0 ? (
          <div className="mt-6 bg-white border border-stone-200 rounded-md p-10 text-center font-sans text-stone-500">No active listings yet — check back soon.</div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        )}
      </div>

      {newsletters.length > 0 && (
        <div className="mt-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Recent Releases</div>
          <h2 className="mt-3 font-serif text-3xl text-stone-900">From the farm</h2>
          <div className="mt-6 space-y-4">
            {newsletters.map((n) => (
              <div key={n.id} className="bg-white border border-stone-200 rounded-md p-6">
                <div className="font-serif text-xl text-stone-900">{n.title}</div>
                <p className="mt-2 text-sm text-stone-600 font-sans whitespace-pre-line">{n.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Testimonials farmerId={farmer.user_id} limit={6} className="mt-16 !px-0" />

      <div className="mt-16">
        <TestimonialForm farmerId={farmer.user_id} farmerName={farmer.farm_name} />
      </div>
    </div>
  );
}
